import CancelIcon from '@mui/icons-material/Cancel';
import { Box, Paper, Popper } from '@mui/material';
import ConfirmDialog from 'Components/ConfirmDialog';
import { showAlertMessage } from 'Features/alertMessage/alertMessageSlice';
import useClickOutside from 'Hooks/useClickOutside';
import { t } from 'i18next';
import { isEmpty } from 'lodash';
import { useRef, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import DropdownInput from './DropdownInput';
import DropdownItemInput from './DropdownItemInput';
import DropdownItemList from './DropdownItemList';
import './GenericParentDropdown.scss';
import ShuffleDialog from './ShuffleDialog';

const GenericParentDropdown = ({
  name,
  options = [],
  parentOptions = [],
  selectedParent,
  selectedGrandParent,
  extraDropdownOptions = [],
  currentLevel = 0,
  isEditable = false,
  rules = {},
  title = 'Manage Items',
  onCreateOrUpdate,
  onDelete,
  onShuffle
}) => {
  const dispatch = useDispatch();
  const { control, setValue } = useFormContext();

  const currentValue = useWatch({ control, name: `${name}Selected` });
  const type = useWatch({ control, name: 'type' });

  const anchorRef = useRef(null);
  const popperRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [newItem, setNewItem] = useState({});
  const [editIndex, setEditIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [deleteDialogState, setDeleteDialogState] = useState({ open: false, item: {} });
  const [shuffleDialog, setShuffleDialog] = useState({
    open: false,
    fromCategory: '',
    selectedItems: [],
    toCategory: []
  });

  // Click outside handling
  useClickOutside({
    isOpen: open,
    anchorRef,
    popperRef,
    onClose: () => setOpen(false)
  });

  // Combined Create and Update function
  const handleCreateOrUpdate = (item) => {
    if (!type && name === 'meetingSubType') {
      dispatch(
        showAlertMessage({
          type: 'error',
          text: t('fieldManagement.selectType')
        })
      );
      return;
    }

    const action = editIndex !== null ? 'update' : 'create';
    onCreateOrUpdate(name, item, selectedParent, selectedGrandParent, action);
    setEditIndex(null);
    setNewItem({});
    setSearchTerm('');
    setValue(`${name}Selected`, {});
  };

  const handleDelete = (item) => {
    setDeleteDialogState({ open: true, item });
  };

  const handleDeleteDialogConfirm = () => {
    onDelete(name, deleteDialogState?.item, selectedParent, selectedGrandParent, 'delete');
    handleDeleteDialogClose();
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogState({ open: false, item: {} });
  };

  const handleEdit = (itemToEdit, idx) => {
    setNewItem(itemToEdit);
    setEditIndex(idx);
    setOpen(true);
  };

  const handleSelect = (item) => {
    setValue(`${name}Selected`, item);
    setOpen(false);
    setSearchTerm('');
  };

  const confirmShuffle = () => {
    onShuffle(name, shuffleDialog, selectedGrandParent);

    setEditIndex(null);
    setNewItem({});
    setSearchTerm('');

    setValue('areaSelected', {});
    setValue('meetingTypeSelected', {});
    setValue('segmentSelected', {});

    setShuffleDialog({ open: false, fromCategory: '', selectedItems: [], toCategory: [] });
  };

  const filteredItems =
    options?.filter((item) => item.value.toLowerCase().includes(searchTerm.toLowerCase())) || [];

  const moveTocategories = parentOptions.filter((c) => c.id !== shuffleDialog.fromCategory);

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ fieldState: { error } }) => (
        <Box>
          <DropdownInput
            anchorRef={anchorRef}
            onClick={() => {
              setOpen(true);
              setSearchTerm(''); // Reset search term when dropdown opens
            }}
            title={title}
            error={error}
            selectedItem={currentValue} // Pass the selected item here
            searchTerm={searchTerm} // Pass search term here
            onSearchChange={(text) => {
              setSearchTerm(text); // Update searchTerm on user input
            }}
            onClear={() => {
              setSearchTerm('');
              setValue(`${name}Selected`, {});
            }}
            disabled={isEmpty(selectedParent) && currentLevel > 0}
          />

          {(currentLevel === 0 || (!isEmpty(selectedParent) && currentLevel > 0)) && (
            <Popper
              open={open}
              anchorEl={anchorRef.current}
              placement="bottom-start"
              style={{ zIndex: 1300, width: 380 }}>
              <Paper
                ref={popperRef}
                sx={{ width: '100%', maxHeight: 300, overflowY: 'auto', p: 1 }}>
                <DropdownItemInput
                  value={!isEmpty(newItem) ? newItem?.value : ''}
                  newItem={newItem}
                  setNewItem={setNewItem}
                  onSubmit={() => handleCreateOrUpdate(newItem)}
                  isEdit={editIndex !== null}
                  label={`Type ${title}`}
                  extraDropdownOptions={extraDropdownOptions}
                />

                <DropdownItemList
                  items={filteredItems}
                  selectedItem={currentValue}
                  selectedParent={selectedParent}
                  isEditable={isEditable}
                  onSelect={handleSelect}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onShuffle={(cat) => {
                    const checkMoveTo = parentOptions.filter((c) => c.id !== cat);
                    if (isEmpty(checkMoveTo)) {
                      dispatch(
                        showAlertMessage({
                          type: 'error',
                          text: t('fieldManagement.canNotReshuffle')
                        })
                      );
                      return;
                    } else {
                      setShuffleDialog({
                        open: true,
                        fromCategory: cat,
                        selectedItems: [],
                        toCategory: []
                      });
                    }
                  }}
                />
              </Paper>
            </Popper>
          )}

          <ShuffleDialog
            open={shuffleDialog.open}
            selectedItems={shuffleDialog.selectedItems}
            fromCategory={shuffleDialog.fromCategory}
            toCategory={shuffleDialog.toCategory}
            setShuffleDialog={setShuffleDialog}
            confirmShuffle={confirmShuffle}
            fromItems={options || []}
            currentParent={selectedParent}
            moveTocategories={moveTocategories}
          />

          <ConfirmDialog
            open={deleteDialogState?.open}
            text={t('fieldManagement.areYouSureToDelete')}
            Icon={CancelIcon}
            successButtonText={t('button.yes')}
            cancelButtonText={t('button.no')}
            handleClose={handleDeleteDialogClose}
            handleYes={handleDeleteDialogConfirm}
            iconColor="red"
            cancelPopup="Yes"
          />
        </Box>
      )}
    />
  );
};

export default GenericParentDropdown;
